import * as React from 'react';
import AppBar from '@material-ui/core/AppBar';
import Button from '@material-ui/core/Button';
import ContractorSearch from './ContractorSearch';
import Logo from './Logo';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import PersonIcon from '@material-ui/icons/Person';
import Toolbar from '@material-ui/core/Toolbar';
import { CompanySearch } from './CompanySearch';
import { DeepReadonly } from 'utility-types';
import { Feature } from 'flagged';
import { FeatureFlagWrapper } from '@pec/aion-ui-core/features/featureFlags/components/FeatureFlagWrapper';
import { IOrganization, OrganizationFeature } from '@pec/aion-ui-core/interfaces/organization';
import { IUserOrganization } from '@pec/aion-ui-core/interfaces/userOrganization';
import { LanguageSwitcherComponent } from '@pec/aion-ui-i18next/components/LanguageSwitcher';
import { localizationFeatureFlag } from '@pec/aion-ui-i18next/constants';
import { Popover } from '@pec/aion-ui-core/components/Popover';
import { Theme, WithStyles, withStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';

export const styles = (theme: Theme) => ({
  appBar: {
    zIndex: theme.zIndex.drawer + 1
  },
  button: {
    margin: theme.spacing(1)
  },
  grow: {
    flexGrow: 1,
    '& a': {
      color: 'white'
    }
  },
  toolbar: {
    paddingLeft: 10
  }
});

export type OwnProps = {
  handleLogOut: () => void;
  userId?: string;
  userEmail: string | null;
  includeCompanySearch: boolean;
  organizationId?: string;
  userOrganizations: DeepReadonly<IUserOrganization[]> | null;
  organization: DeepReadonly<IOrganization> | null;
  hasFetchedUserOrganizations: boolean;
};

export type Props = WithStyles<typeof styles> & OwnProps;

export type State = {
  anchorEl?: HTMLElement;
};

const DesktopAppBarComponent: React.FC<Props> = ({
  classes,
  handleLogOut,
  userId,
  includeCompanySearch,
  organizationId,
  userOrganizations,
  organization,
  hasFetchedUserOrganizations,
  userEmail
}) => {
  const { t } = useTranslation();
  const vf1MyProfileLink = '/?hint=vf1profile';
  const myAccountLink = '/identity/Manage/Index';

  const showQuickSearch =
    organization?.features.includes(OrganizationFeature.Client) &&
    [OrganizationFeature.ComplianceUK, OrganizationFeature.ComplianceAUS].some(value =>
      organization?.features.includes(value)
    );

  return (
    <AppBar position="fixed" className={classes.appBar}>
      <Toolbar className={classes.toolbar}>
        <div className={classes.grow}>
          {hasFetchedUserOrganizations && (
            <Logo organizationId={organizationId} userOrganizations={userOrganizations} organization={organization} />
          )}
        </div>
        {showQuickSearch && <ContractorSearch organization={organization} />}
        {includeCompanySearch && <CompanySearch />}
        <FeatureFlagWrapper>
          <Feature
            name={localizationFeatureFlag}
            render={(isEnabled: boolean) => isEnabled && <LanguageSwitcherComponent userId={userId} />}
          />
        </FeatureFlagWrapper>
        <Popover
          id="menu-appbar"
          render={({ buttonProps, menuProps }) => (
            <div>
              <Button {...buttonProps} color="inherit" className={classes.button}>
                <PersonIcon /> {userEmail}
              </Button>
              <Menu
                {...menuProps}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
              >
                <FeatureFlagWrapper>
                  <Feature
                    name={'VF1MyProfile'}
                    render={(isEnabled: boolean) => (
                      <MenuItem component="a" href={isEnabled ? vf1MyProfileLink : myAccountLink}>
                        {t('components.desktopAppBar.veriforceProfile', 'VeriforceOne Profile')}
                      </MenuItem>
                    )}
                  />
                </FeatureFlagWrapper>
                <MenuItem component="a" href="/user">
                  {t('components.desktopAppBar.myProfile', 'My Profile')}
                </MenuItem>
                <MenuItem component="a" href="/eLearning">
                  {t('components.desktopAppBar.eLearning', 'eLearning')}
                </MenuItem>
                <MenuItem component="a" onClick={handleLogOut}>
                  {t('components.desktopAppBar.logOut', 'Log Out')}
                </MenuItem>
              </Menu>
            </div>
          )}
        />
      </Toolbar>
    </AppBar>
  );
};

export const DesktopAppBar = withStyles(styles)(DesktopAppBarComponent);
